<template>
  <header class="bg-[#FF6333] p-4">
    <div class="flex items-center gap-4">
      <div class="logo">
        <router-link :to="{ name: 'tickets-list' }">
          <figure style="height: 35px">
            <img
              src="/frontend/static/norm_logo.svg"
              alt="Logo"
              style="height: 100%"
            />
          </figure>
        </router-link>
      </div>
      <div class="grow gap-2">
        <template v-for="item in nav">
          <router-link
            v-if="
              typeof routes[item.name] === 'string' &&
              hasPermission(item.permission)
            "
            :key="item.name"
            class="nav-link"
            :to="{ name: routes[item.name] }"
          >
            {{ item.name }}
          </router-link>
          <a
            v-else-if="
              routes[item.name]?.href && hasPermission(item.permission)
            "
            :key="`A${item.name}`"
            class="nav-link"
            :href="routes[item.name].href"
          >
            {{ item.name }}
          </a>
        </template>
      </div>
      <div>
        <div class="text-sm flex gap-2">
          <div class="flex items-center tw-mx-2">
            <span>{{ auth.username }}</span>
          </div>

          <button
            class="text-xs bg-gray-200 text-black rounded-sm p-1 whitespace-nowrap"
            @click="auth.clearUserData()"
          >
            Log uit
          </button>
        </div>
      </div>
    </div>
  </header>
</template>

<script setup>
import { useAuthStore } from '@/auth'
import { erpBaseUrl } from '@/erpMenu'

const auth = useAuthStore()

const adminPermissions = ['erp_admins', 'administrators']

const hasPermission = (targetPermissions, allowAdmins = true) => {
  const userPermissions = auth.permissions

  if (targetPermissions.length === 0) return true
  if (!userPermissions) return false

  const permissionList = Object.values(userPermissions).reduce((acc, arr) => [
    ...acc,
    ...arr,
  ])

  if (
    allowAdmins &&
    permissionList.some((permission) => adminPermissions.includes(permission))
  )
    return true

  return permissionList.some((permission) =>
    targetPermissions.includes(permission)
  )
}

// this is separated so the nav can be copied from ERP
const routes = {
  Clients: { href: `${erpBaseUrl}/#/clients` },
  Tasks: { href: `${erpBaseUrl}/#/tasks` },
  Groepen: { href: `${erpBaseUrl}/#/groups` },
  Gebouwen: { href: `${erpBaseUrl}/#/v2/buildings` },
  Alarmen: { href: `${erpBaseUrl}/#/v2/alarms` },
  Cases: { href: `${erpBaseUrl}/#/v2/cases` },
  Polling: { href: `${erpBaseUrl}/#/v2/polling` },
  'Purchase Orders': { href: `${erpBaseUrl}/#/purchase-orders` },
  Projecten: 'projects-list',
  'Finished tasks': 'finished-tasks',
  Gebruikers: 'users-list',
  'Call Centre': { href: `${erpBaseUrl}/#/call-centre` },
  PlanBaas: 'planner',
  Tickets: 'tickets-list',
  // The partner list needs its own right: someone with only the prijsopgave right
  // lands on the prijsopgaven tab instead. A getter, so the template reads it live.
  get IPAs() {
    return hasPermission(['Tickets:view_installation_partners'])
      ? 'installation-partner-list'
      : 'ipa-quotes'
  },
  Commercie: 'customer-list',
  Logistiek: 'logistics',
  'Energieleveren.nl': 'energieleveren',
}

// !! this should be the same as the list in ERP
const nav = [
  { name: 'Clients', permission: 'ERP_VIEW_SCREEN_CLIENTS' },
  { name: 'Tasks', permission: 'ERP_VIEW_SCREEN_TASKS' },
  { name: 'Call Centre', permission: 'ERP_VIEW_SCREEN_CALL_CENTRE' },
  { name: 'Groepen', permission: 'ERP_VIEW_SCREEN_CLIENTS' },
  { name: 'Gebouwen', permission: ['ERP:view_buildings'] },
  { name: 'Alarmen', permission: ['Eleena:view_alarms'] },
  { name: 'Cases', permission: ['Tickets:view_cases'] },
  { name: 'Polling', permission: ['ERP:view_polling'] },
  { name: 'Purchase Orders', permission: ['ERP:view_purchase_orders'] },
  { name: 'Projecten', permission: 'ERP_VIEW_SCREEN_PROJECTS' },
  { name: 'Finished tasks', permission: ['Tickets:view_locations'] },
  { name: 'Gebruikers', permission: ['ERP:list_users'] },
  { name: 'PlanBaas', permission: ['Tickets:view_planner'] },
  { name: 'Tickets', permission: ['tickets_v2'] },
  // The partners and their prijsopgaven, as tabs in InstallationPartnersView.
  {
    name: 'IPAs',
    permission: [
      'Tickets:view_installation_partners',
      'Tickets:view_installer_quotes',
    ],
  },
  // Any of the three tabs the section holds is reason enough to show it; which tabs
  // then appear is decided in CustomersView with the same three rights.
  {
    name: 'Commercie',
    permission: [
      'Tickets:list_customers',
      'Tickets:view_exact_bouw_prices',
      'Tickets:view_customer_quotes',
    ],
  },
  // Logistiek groups the former Voorraad, Artikelen and Bestellingen entries as tabs.
  {
    name: 'Logistiek',
    permission: [
      'Tickets:view_warehouses',
      'Tickets:view_items',
      'partner_orders_manager',
      'partner_orders_basic',
    ],
  },
  { name: 'Energieleveren.nl', permission: ['Tickets:view_energieleveren'] },
]
</script>

<style scoped>
.nav-link {
  display: inline-block;
  position: relative;
  color: #5d0e07;
  font-family: BlinkMacSystemFont, -apple-system, 'Segoe UI', 'Roboto', 'Oxygen',
    'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
    'Helvetica', 'Arial', sans-serif;
  line-height: 1.5;
  margin-left: 0.5rem;
  margin-right: 0.5rem;
  font-weight: 600;
}

.nav-link.router-link-active {
  color: #fdd6ba;
}

.nav-link.router-link-active::after {
  content: '';
  position: absolute;
  bottom: -5px;
  width: 20px;
  height: 2px;
  left: 50%;
  transform: translateX(-50%);
  background-color: #fdd6ba;
}
</style>
